import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import AddIcon from "@mui/icons-material/Add";
import { icons } from "../../svg";
import { DialogStyleProfile, dialogStyles } from "./dialogStyles";

export type AlertSeverity = "warning" | "error" | "info" | "success";

export interface GenericDialogPanelProps {
  open: boolean;
  styleProfile?: DialogStyleProfile;
  title?: string;
  severity?: AlertSeverity;
  alertTitle?: React.ReactNode;
  alertSubtitle?: React.ReactNode;
  cancelLabel?: string;
  confirmLabel?: string;
  confirmIcon?: React.ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
  children?: React.ReactNode;
}

export const GenericDialogPanel: React.FC<GenericDialogPanelProps> = ({
  open,
  styleProfile = dialogStyles.bottomSheetWarning, // Default style profile
  title,
  severity = "warning",
  alertTitle,
  alertSubtitle,
  cancelLabel = "Cancel",
  confirmLabel = "Confirm",
  confirmIcon = <AddIcon />,
  onCancel,
  onConfirm,
  children,
}) => {
  const overlayRootRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof document === "undefined") return;

    let root = document.getElementById("mobile-overlay-root");
    if (!root) {
      root = document.createElement("div");
      root.id = "mobile-overlay-root";
      root.style.position = "fixed";
      root.style.top = "0";
      root.style.left = "0";
      root.style.right = "0";
      root.style.bottom = "0";
      root.style.zIndex = "100000";
      document.body.appendChild(root);
    }

    root.style.pointerEvents = "none";
    overlayRootRef.current = root as HTMLElement;
  }, []);

  if (!open) return null;

  const panel = (
    <Box
      onClick={(e) => e.target === e.currentTarget && onCancel()}
      role="presentation"
      sx={styleProfile.overlay}
    >
      <Box role="dialog" aria-modal="true" sx={styleProfile.sheet}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
          {title && (
            <Typography variant="h6" sx={styleProfile.title}>
              {title}
            </Typography>
          )}

          {(alertTitle || alertSubtitle) && (
            <Alert
              severity={severity}
              variant="outlined"
              icon={
                <img
                  src={icons.ExclamationMarkTriangle}
                  alt={`${severity} icon`}
                  style={{ width: 20, height: 20 }}
                />
              }
              sx={styleProfile.alert}
            >
              {alertTitle && <AlertTitle sx={{ margin: 0, fontWeight: 500, fontSize: "16px" }}>{alertTitle}</AlertTitle>}
              {alertSubtitle && <Typography sx={{ fontSize: "12px", fontWeight: 400 }}>{alertSubtitle}</Typography>}
            </Alert>
          )}

          {children}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "8px", width: "100%" }}>
            {cancelLabel && (
              <Button variant="text" onClick={onCancel} sx={styleProfile.cancelButton}>
                {cancelLabel}
              </Button>
            )}
            {confirmLabel && (
              <Button variant="contained" onClick={onConfirm} startIcon={confirmIcon} sx={styleProfile.confirmButton}>
                {confirmLabel}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return overlayRootRef.current ? createPortal(panel, overlayRootRef.current) : panel;
};

export default GenericDialogPanel;